import {BaseQueryFn, FetchArgs, fetchBaseQuery, FetchBaseQueryError} from '@reduxjs/toolkit/query';

// eslint-disable-next-line no-restricted-imports
import {not, path} from 'ramda';

import {browserStorageKey} from '@dms/config';
import {getWorkspaceFromUri} from '@dms/routes';

import {BaseQueryExtraOptions, parseZodSchema, ParseZodSchemaQueryInfo} from 'shared/web';

import {captureBaseQueryError} from './captureBaseQueryError';
import {isCI} from './constants/isCI';
import {isDev} from './constants/isDev';

const fetchBaseQueryInstance = fetchBaseQuery({
  baseUrl: process.env.CHAT_FE_API_URL,
  credentials: 'include',
  prepareHeaders: (previousHeaders, {getState}) => {
    const headers = new Headers(previousHeaders);

    const accessToken = sessionStorage.getItem(browserStorageKey.ACCESS_TOKEN);
    const uri = getWorkspaceFromUri();
    const activeBranchId = path<string>(['branch', 'activeBranchId'], getState());
    const acceptLanguage = localStorage.getItem(browserStorageKey.LAST_KNOWN_LANGUAGE);

    if (accessToken) {
      headers.set('authorization', `Bearer ${accessToken}`);
    }
    if (activeBranchId) {
      headers.set('X-Branch', activeBranchId);
    }
    if (uri.workspace) {
      headers.set('X-Workspace', uri.workspace);
    }

    if (acceptLanguage) {
      headers.set('accept-language', acceptLanguage);
    }

    headers.set('X-Country-Format', 'iso');
    headers.set('x-request-id', window.crypto.randomUUID());

    return headers;
  },
});

export interface CustomFetchArgs extends FetchArgs {
  queryArg?: Record<string, unknown> | void;
}

export const chatBaseQuery: BaseQueryFn<
  CustomFetchArgs,
  unknown,
  FetchBaseQueryError,
  BaseQueryExtraOptions
> = async (args, api, extraOptions) => {
  const queryInfo: Omit<ParseZodSchemaQueryInfo, 'schemaType'> = {
    method: args.method,
    endpointName: api.endpoint,
    url: args.url,
  };

  if (isDev && not(isCI) && extraOptions && extraOptions.requestSchema) {
    parseZodSchema({
      data: args.queryArg,
      schema: extraOptions.requestSchema,
      queryInfo: {
        ...queryInfo,
        schemaType: 'request',
      },
    });
  }

  const result = await fetchBaseQueryInstance(args, api, extraOptions);

  if (isDev && not(isCI) && extraOptions && extraOptions.responseSchema && 'data' in result) {
    parseZodSchema({
      data: result.data,
      schema: extraOptions.responseSchema,
      queryInfo: {
        ...queryInfo,
        schemaType: 'response',
      },
    });
  }

  if (result.error && typeof result.error.status === 'number' && result.error.status >= 500) {
    captureBaseQueryError(result.error, args, api);
  }

  return result;
};
